import { useCopyText } from '../hooks/useCopyText';
import type {
  InAppBrowserInfo,
  InAppBrowserPlatform,
} from '../hooks/useInAppBrowser';

interface InAppBrowserOverlayProps {
  browserName: InAppBrowserInfo['browserName'];
  platform: InAppBrowserPlatform;
}

function getExternalBrowserUrl(
  platform: InAppBrowserPlatform,
  currentUrl: string
): string | null {
  try {
    const url = new URL(currentUrl);
    const pathAndQuery = `${url.host}${url.pathname}${url.search}${url.hash}`;

    if (platform === 'android') {
      return `intent://${pathAndQuery}#Intent;scheme=https;action=android.intent.action.VIEW;category=android.intent.category.BROWSABLE;end;`;
    }

    if (platform === 'ios') {
      return `x-safari-https://${pathAndQuery}`;
    }

    return null;
  } catch {
    return null;
  }
}

export default function InAppBrowserOverlay({
  browserName,
  platform,
}: InAppBrowserOverlayProps) {
  const { copyToClipboard, copied } = useCopyText();
  const currentUrl = window.location.href;
  const externalUrl = getExternalBrowserUrl(
    platform,
    currentUrl
  );
  const displayName = browserName ?? 'this app';

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-ink-900/40"
      data-testid="in-app-browser-overlay"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="iab-title"
    >
      <div className="paper-card w-full max-w-md overflow-hidden">
        <div className="p-6 space-y-4">
          <div className="space-y-1">
            <h2
              id="iab-title"
              className="font-serif text-xl font-bold text-ink-900"
            >
              Open in your browser
            </h2>
            <p className="font-mono text-xs text-ink-400">
              Can't stay signed in here
            </p>
          </div>

          <p className="text-sm text-ink-700 leading-relaxed">
            CommonCal can't keep you signed in inside{' '}
            {displayName}'s built-in browser. Open this link
            in your default browser to continue.
          </p>

          <div className="flex flex-col gap-3">
            {externalUrl && (
              <button
                type="button"
                onClick={() => {
                  window.location.href = externalUrl;
                }}
                className="btn-primary w-full"
              >
                Open in browser
              </button>
            )}

            <button
              type="button"
              onClick={() => copyToClipboard(currentUrl)}
              className="btn-ghost w-full"
            >
              {copied ? 'Copied!' : 'Copy link'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
